'use client'
import { useState, useEffect, useCallback } from 'react';
import { appStorage } from '../utils/appStorage';

const DEBUG_KEY = '__debug_storage_panel';
const USER_PINS_KEY = '__debug_user_pins';

function getDebugEnabled() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.has('debug')) {
    const val = params.get('debug');
    if (val === '0' || val === 'false') {
      sessionStorage.removeItem(DEBUG_KEY);
      return false;
    }
    sessionStorage.setItem(DEBUG_KEY, '1');
    return true;
  }
  return sessionStorage.getItem(DEBUG_KEY) === '1';
}

function resolveValue(path) {
  try {
    const [store, ...keyParts] = path.split('.');
    const key = keyParts.join('.');
    let raw = null;

    if (store === 'ls' || store === 'localStorage') {
      raw = localStorage.getItem(key);
    } else if (store === 'ss' || store === 'sessionStorage') {
      raw = sessionStorage.getItem(key);
    } else if (store === 'cookie') {
      const match = document.cookie.split(';').find(c => c.trim().startsWith(key + '='));
      raw = match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
    } else if (store === 'app') {
      if (key === 'appName') raw = appStorage.getAppName();
      else if (key === 'appDescription') raw = appStorage.getAppDescription();
      else if (key === 'appId') raw = appStorage.getAppId();
      else if (key === 'appTitle') raw = appStorage.getAppTitle();
      else if (key === 'settingsKey') {
        const an = appStorage.getAppName();
        raw = an ? `${an}_settings` : 'smartEnglishSettings';
      }
      else raw = `(unknown: ${key})`;
    } else {
      return `(unknown store: ${store})`;
    }

    if (raw === null) return '(null)';
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object') return JSON.stringify(parsed, null, 1);
      return String(parsed);
    } catch { return raw; }
  } catch (e) { return `(error: ${e.message})`; }
}

function getCookies() {
  if (typeof document === 'undefined') return {};
  const cookies = {};
  document.cookie.split(';').forEach(c => {
    const [k, ...v] = c.trim().split('=');
    if (k) cookies[k] = decodeURIComponent(v.join('='));
  });
  return cookies;
}

function getLocalStorageItems() {
  const items = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    let val = localStorage.getItem(key);
    try {
      val = JSON.stringify(JSON.parse(val)).substring(0, 120);
      if (val.length >= 120) val += '...';
    } catch {}
    items[key] = val;
  }
  return items;
}

function getSessionStorageItems() {
  const items = {};
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key.startsWith('__debug')) continue;
    items[key] = sessionStorage.getItem(key);
  }
  return items;
}

async function getIndexedDBNames() {
  try { return (await indexedDB.databases()).map(db => `${db.name} (v${db.version})`); }
  catch { return ['(API not supported)']; }
}
async function getSWCacheNames() {
  try { return await caches.keys(); } catch { return []; }
}
async function getSWRegistrations() {
  try { return (await navigator.serviceWorker.getRegistrations()).map(r => r.scope); }
  catch { return []; }
}

function loadUserPins() {
  try { return JSON.parse(sessionStorage.getItem(USER_PINS_KEY) || '[]'); }
  catch { return []; }
}
function saveUserPins(pins) {
  sessionStorage.setItem(USER_PINS_KEY, JSON.stringify(pins));
}

function buildCopyText(pins, label) {
  if (pins.length === 0) return '';
  const lines = [`[${label}] ${new Date().toLocaleString()}`];
  for (const pin of pins) lines.push(`${pin} = ${resolveValue(pin)}`);
  return lines.join('\n');
}

function buildAllCopyText(data, userPins, aiPins) {
  const lines = [`=== Storage Debug === ${new Date().toLocaleString()}`];
  if (userPins.length > 0) {
    lines.push('\n--- User Pins ---');
    for (const p of userPins) lines.push(`${p} = ${resolveValue(p)}`);
  }
  if (aiPins.length > 0) {
    lines.push('\n--- AI Pins ---');
    for (const p of aiPins) lines.push(`${p} = ${resolveValue(p)}`);
  }
  lines.push('\n--- App Context ---');
  lines.push(`appName = ${appStorage.getAppName() || '(null)'}`);
  lines.push(`appDescription = ${appStorage.getAppDescription() || '(null)'}`);
  const an = appStorage.getAppName();
  lines.push(`settingsKey = ${an ? `${an}_settings` : 'smartEnglishSettings'}`);
  if (data) {
    lines.push(`\n--- Cookies (${Object.keys(data.cookies).length}) ---`);
    for (const [k, v] of Object.entries(data.cookies)) lines.push(`${k} = ${v}`);
    lines.push(`\n--- localStorage (${Object.keys(data.ls).length}) ---`);
    for (const [k, v] of Object.entries(data.ls)) lines.push(`${k} = ${v}`);
    lines.push(`\n--- sessionStorage (${Object.keys(data.ss).length}) ---`);
    for (const [k, v] of Object.entries(data.ss)) lines.push(`${k} = ${v}`);
    lines.push(`\n--- IndexedDB (${data.idb.length}) ---`);
    for (const n of data.idb) lines.push(n);
    lines.push(`\n--- Cache Storage (${data.cache.length}) ---`);
    for (const c of data.cache) lines.push(c);
  }
  return lines.join('\n');
}

// --- UI Components ---
function CopyButton({ getText, label = '📋' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={async () => {
      try { await navigator.clipboard.writeText(getText()); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
    }} style={{
      background: 'none', border: '1px solid #555', color: copied ? '#4caf50' : '#aaa',
      cursor: 'pointer', fontSize: 10, borderRadius: 3, padding: '1px 4px', marginLeft: 4,
    }}>
      {copied ? '✓' : label}
    </button>
  );
}

function Section({ title, children, defaultOpen = true, extra = null }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div onClick={() => setOpen(!open)} style={{
          cursor: 'pointer', fontWeight: 'bold', fontSize: 11, color: '#4fc3f7', userSelect: 'none', flex: 1,
        }}>
          {open ? '▼' : '▶'} {title}
        </div>
        {extra}
      </div>
      {open && <div style={{ paddingLeft: 8, fontSize: 10 }}>{children}</div>}
    </div>
  );
}

function KVList({ data }) {
  const entries = Object.entries(data);
  if (entries.length === 0) return <div style={{ color: '#888', fontSize: 10 }}>(empty)</div>;
  return entries.map(([k, v]) => (
    <div key={k} style={{ wordBreak: 'break-all', lineHeight: 1.3, marginBottom: 2 }}>
      <span style={{ color: '#81c784' }}>{k}</span>
      <span style={{ color: '#666' }}> = </span>
      <span style={{ color: '#e0e0e0' }}>{v}</span>
    </div>
  ));
}

function PinList({ pins, color, onRemove }) {
  return pins.map((pin, i) => (
    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 2 }}>
      {onRemove && (
        <span onClick={() => onRemove(i)} style={{ cursor: 'pointer', color: '#f44', fontSize: 9, marginRight: 4, userSelect: 'none' }}>✕</span>
      )}
      <div style={{ flex: 1, wordBreak: 'break-all', lineHeight: 1.3 }}>
        <span style={{ color }}>{pin}</span>
        <span style={{ color: '#666' }}> = </span>
        <span style={{ color: '#e0e0e0' }}>{resolveValue(pin)}</span>
      </div>
    </div>
  ));
}

export default function DebugStoragePanel() {
  const [enabled, setEnabled] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [data, setData] = useState(null);
  const [userPins, setUserPins] = useState([]);
  const [aiPins, setAiPins] = useState([]);
  const [addInput, setAddInput] = useState('');

  useEffect(() => { setEnabled(getDebugEnabled()); }, []);

  // AI Pinsをサーバーから取得
  const fetchAiPins = useCallback(async () => {
    try {
      const res = await fetch('/debug-pin');
      if (res.ok) {
        const json = await res.json();
        setAiPins(json.pins || []);
      }
    } catch {}
  }, []);

  const refresh = useCallback(async () => {
    const cookies = getCookies();
    const ls = getLocalStorageItems();
    const ss = getSessionStorageItems();
    const idb = await getIndexedDBNames();
    const sw = await getSWRegistrations();
    const cache = await getSWCacheNames();
    setData({ cookies, ls, ss, idb, sw, cache, ts: new Date().toLocaleTimeString() });
    setUserPins(loadUserPins());
    await fetchAiPins();
  }, [fetchAiPins]);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const interval = setInterval(refresh, 3000);
    return () => clearInterval(interval);
  }, [enabled, refresh]);

  const addUserPin = () => {
    if (!addInput.trim()) return;
    const newPins = [...userPins, addInput.trim()];
    saveUserPins(newPins);
    setUserPins(newPins);
    setAddInput('');
  };

  const removeUserPin = (idx) => {
    const newPins = userPins.filter((_, i) => i !== idx);
    saveUserPins(newPins);
    setUserPins(newPins);
  };

  if (!enabled) return null;

  if (minimized) {
    return (
      <div onClick={() => setMinimized(false)} style={{
        position: 'fixed', bottom: 8, right: 8, zIndex: 99999,
        background: '#1e1e1e', color: '#4fc3f7', border: '1px solid #333',
        borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11, opacity: 0.8,
      }}>
        🔧 Debug
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', bottom: 8, right: 8, zIndex: 99999,
      width: 400, maxHeight: '70vh', overflow: 'auto',
      background: '#1e1e1eee', color: '#e0e0e0', border: '1px solid #333',
      borderRadius: 6, padding: 8, fontSize: 10, fontFamily: 'Consolas, monospace',
      boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontWeight: 'bold', fontSize: 12, color: '#4fc3f7' }}>
          Storage Debug {data?.ts && <span style={{ color: '#888', fontSize: 9 }}>({data.ts})</span>}
        </span>
        <span>
          <CopyButton getText={() => buildAllCopyText(data, userPins, aiPins)} label="📋 All" />
          <button onClick={refresh} style={{ background: 'none', border: 'none', color: '#4fc3f7', cursor: 'pointer', fontSize: 11, marginLeft: 4 }}>↻</button>
          <button onClick={() => setMinimized(true)} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 11, marginLeft: 4 }}>_</button>
          <button onClick={() => { sessionStorage.removeItem(DEBUG_KEY); setEnabled(false); }} style={{ background: 'none', border: 'none', color: '#f44', cursor: 'pointer', fontSize: 11, marginLeft: 4 }}>✕</button>
        </span>
      </div>

      {/* User Pins */}
      <Section
        title={`📌 User Pins (${userPins.length})`}
        defaultOpen={userPins.length > 0}
        extra={<CopyButton getText={() => buildCopyText(userPins, 'User Pins')} label="📋" />}
      >
        <PinList pins={userPins} color="#ffb74d" onRemove={removeUserPin} />
        <div style={{ display: 'flex', marginTop: 4 }}>
          <input
            value={addInput}
            onChange={e => setAddInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addUserPin()}
            placeholder="ls.key / ss.key / cookie.key / app.appName"
            style={{
              flex: 1, background: '#333', border: '1px solid #555', color: '#e0e0e0',
              fontSize: 10, padding: '2px 4px', borderRadius: 3, fontFamily: 'Consolas, monospace',
            }}
          />
          <button onClick={addUserPin} style={{
            background: '#333', border: '1px solid #555', color: '#4fc3f7',
            cursor: 'pointer', fontSize: 10, padding: '2px 6px', marginLeft: 4, borderRadius: 3,
          }}>+</button>
        </div>
      </Section>

      {/* AI Pins（サーバーから取得） */}
      <Section
        title={`🤖 AI Pins (${aiPins.length})`}
        defaultOpen={aiPins.length > 0}
        extra={<CopyButton getText={() => buildCopyText(aiPins, 'AI Pins')} label="📋" />}
      >
        <PinList pins={aiPins} color="#ce93d8" />
        {aiPins.length === 0 && <div style={{ color: '#888' }}>AI側から /debug-pin に POST で設定</div>}
      </Section>

      {data && (
        <>
          <Section title="App Context">
            <div><span style={{ color: '#81c784' }}>appName</span> = <span>{appStorage.getAppName() || '(null)'}</span></div>
            <div><span style={{ color: '#81c784' }}>appDesc</span> = <span>{appStorage.getAppDescription() || '(null)'}</span></div>
            <div><span style={{ color: '#81c784' }}>settingsKey</span> = <span>{appStorage.getAppName() ? `${appStorage.getAppName()}_settings` : 'smartEnglishSettings'}</span></div>
          </Section>

          <Section title={`Cookies (${Object.keys(data.cookies).length})`} defaultOpen={false}>
            <KVList data={data.cookies} />
          </Section>

          <Section title={`localStorage (${Object.keys(data.ls).length})`}>
            <KVList data={data.ls} />
          </Section>

          <Section title={`sessionStorage (${Object.keys(data.ss).length})`} defaultOpen={false}>
            <KVList data={data.ss} />
          </Section>

          <Section title={`IndexedDB (${data.idb.length})`} defaultOpen={false}>
            {data.idb.map((n, i) => <div key={i}>{n}</div>)}
          </Section>

          <Section title={`Service Workers (${data.sw.length})`} defaultOpen={false}>
            {data.sw.length === 0 ? <div style={{ color: '#888' }}>(none)</div> : data.sw.map((s, i) => <div key={i}>{s}</div>)}
          </Section>

          <Section title={`Cache Storage (${data.cache.length})`} defaultOpen={false}>
            {data.cache.length === 0 ? <div style={{ color: '#888' }}>(none)</div> : data.cache.map((c, i) => <div key={i}>{c}</div>)}
          </Section>
        </>
      )}
    </div>
  );
}
